import { Injectable, inject } from '@angular/core';
import { Observable, catchError, of, forkJoin, map } from 'rxjs';

import { LoggingService } from '../../logging/logging.service';
import { CacheStore, CacheStoreConfig } from '../models/cache-models';

/**
 * Central manager for all cache stores.
 * Provides registration, lookup, and coordination of store operations.
 */
@Injectable({
  providedIn: 'root'
})
export class CacheManagerService {
  private readonly logger = inject(LoggingService);

  // Registry of cache stores
  private stores = new Map<string, CacheStore<unknown>>();

  /**
   * Register a cache store.
   *
   * @param config Configuration for the store.
   * @param store The store instance to register.
   * @returns Observable that completes when registration is done.
   */
  public registerStore<T>(config: CacheStoreConfig, store: CacheStore<T>): Observable<boolean> {
    if (this.stores.has(config.storeName)) {
      this.logger.warn(`Store ${config.storeName} is already registered. Overwriting.`);
    }

    this.stores.set(config.storeName, store);

    this.logger.debug(`Registered cache store: ${config.storeName}`);

    return of(true);
  }

  /**
   * Get a store by name.
   *
   * @param storeName The name of the store to retrieve.
   * @returns The store instance or null if not found.
   */
  public getStore<T>(storeName: string): CacheStore<T> | null {
    const store = this.stores.get(storeName) as CacheStore<T>;

    if (!store) {
      this.logger.warn(`Store ${storeName} not found`);
      return null;
    }

    return store;
  }

  /**
   * Get names of all registered stores.
   *
   * @returns Array of store names.
   */
  public getAllStores(): string[] {
    return Array.from(this.stores.keys());
  }

  /**
   * Clear all registered stores.
   *
   * @returns Observable that completes when clearing is done.
   */
  public clearAll(): Observable<boolean> {
    if (this.stores.size === 0) {
      return of(true);
    }

    this.logger.debug(`Clearing ${this.stores.size} cache stores`);

    const clearOperations = Array.from(this.stores.entries()).map(([storeName, store]) => {
      return store.clear().pipe(
        // Log each operation but don't error out the entire chain
        catchError(error => {
          this.logger.error(`Error clearing store ${storeName}:`, error);
          return of(false);
        })
      );
    });

    // If no stores, just return success
    if (clearOperations.length === 0) {
      return of(true);
    }

    // Wait for all clear operations to complete
    return forkJoin(clearOperations).pipe(
      map(results => results.every(Boolean)),
      catchError(error => {
        this.logger.error('Error clearing all stores:', error);
        return of(false);
      })
    );
  }
}
